import { Theme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import CompanyColumnCell from "_shared/teams/cells/company-column-cell";
import LeaderColumnCell from "_shared/teams/cells/leader-column-cell";
import MembersColumnCell from "_shared/teams/cells/members-column-cell";
import {
  LowerTopToolbarContainer,
  TableTitle,
  TableTopToolbarContainer,
  TeamsTabs,
  ToolbarButtons,
  ToolbarButtonsContainer,
} from "_shared/teams/teams-page";
import { PageTab } from "_shared/teams/teams-tabs";
import _ from "lodash";
import { populateIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  MaterialReactTable,
  type MRT_PaginationState as PaginationState,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable,
} from "material-react-table";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import * as React from "react";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate } from "react-router-dom";
import { useSession } from "users/__session/session-context";
import {
  getErrorLoadingTeamsByQuery,
  getIsLoadingTeamsByQuery,
  getTeamsByQuery,
  getTeamsQueryByInterface,
  populateTeamsByQuery,
  updateTeamsInterfaceQuery,
} from "work/work-agreements/teams/vendor-team/_store/vendor-teams-redux-slice";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

export default function ActiveTeams() {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [globalFilter, setGlobalFilter] = React.useState();
  const [activeTab, setActiveTab] = React.useState(PageTab.Templates);

  const routeParams = useLoaderData();
  const navigate = useNavigate();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const dispatch = useAttorneyHubDispatch();

  const interfaceName = "ActiveTeamsPage";
  const workingForMe = activeTab === PageTab.TeamsWorkingForMe;
  const vendorTeamsQuery = useSelector(getTeamsQueryByInterface(interfaceName));
  const vendorTeamsResponse = useSelector(getTeamsByQuery(interfaceName, workingForMe));
  const isLoadingVendorTeams = useSelector(getIsLoadingTeamsByQuery(interfaceName, workingForMe));
  const errorLoadingVendorTeams = useSelector(getErrorLoadingTeamsByQuery(interfaceName, workingForMe));

  const vendorTeamUserIds = getVendorTeamUserIds(vendorTeamsResponse || []);

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (vendorTeamsQuery) return;
    dispatch(updateTeamsInterfaceQuery({ interfaceName, workingForMe }));
  }, []);

  useEffect(() => {
    if (vendorTeamsQuery && !isLoadingVendorTeams && !errorLoadingVendorTeams) {
      dispatch(
        populateTeamsByQuery({
          session,
          workingForMe,
          interfaceName,
        }),
      );
    }
  }, [vendorTeamsQuery, workingForMe]);

  useEffect(() => {
    if (vendorTeamUserIds.length === 0) return;
    dispatch(
      populateIndividualsByUserIds({
        session,
        userIds: vendorTeamUserIds,
      }),
    );
  }, [vendorTeamUserIds]);

  useEffect(() => {
    if (!session.user?.legalEntityId || !session.user?.isCompanyManager) return;
    if (routeParams.tab) {
      setActiveTab(routeParams.tab as PageTab);
    } else {
      navigate(`/teams/${PageTab.Templates}`);
    }
  }, [routeParams, columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting]);

  const columnHelper = createMRTColumnHelper<VendorTeam>();
  const columns = React.useMemo(() => [
    columnHelper.accessor((team) => <CompanyColumnCell activeTeam={team} />, {
      header: "Company",
      id: "company",
      enableSorting: false,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((team) => <LeaderColumnCell activeTeam={team} />, {
      header: "Leader",
      id: "leader",
      enableSorting: false,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((team) => <MembersColumnCell activeTeam={team} />, {
      header: "Members",
      id: "members",
      enableSorting: false,
      enableColumnFilter: false,
    }),
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: vendorTeamsResponse ?? [],
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: () => ({
      onClick: () => {},
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <ToolbarButtonsContainer>
        <ToolbarButtons>
          <ToggleGlobalFilterButton table={table} />
          <ToggleFiltersButton table={table} />
          <ShowHideColumnsButton table={table} />
          <ToggleDensePaddingButton table={table} />
          <ToggleFullScreenButton table={table} />
        </ToolbarButtons>
      </ToolbarButtonsContainer>
    ),
    renderTopToolbarCustomActions: () => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Teams</TableTitle>
          <LowerTopToolbarContainer>
            <TeamsTabs activeTab={activeTab} />
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row, index) =>
      `${row.leader?.userId?.value || "no-leader"}-${row.members?.map((m) => m.userId.value).join("-")}-${index}`,
    enableRowActions: false,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: [] },
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: errorLoadingVendorTeams
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: vendorTeamsResponse?.length ?? 0,
    state: {
      columnFilters,
      globalFilter,
      isLoading: !vendorTeamsResponse?.length && isLoadingVendorTeams,
      pagination,
      showAlertBanner: Boolean(errorLoadingVendorTeams),
      showProgressBars: isLoadingVendorTeams,
      sorting,
      showSkeletons: !vendorTeamsResponse?.length && isLoadingVendorTeams,
    },
  });

  if (!sessionPermissions?.includesAccessToTeams) {
    return <NotAuthorized />;
  }

  return <MaterialReactTable table={table} />;
}

function getVendorTeamUserIds(teams: VendorTeam[]): Guid[] {
  if (teams.length === 0) return [];
  return _.uniqBy(
    teams.flatMap((team) => [
      ...(team.leader?.userId ? [team.leader.userId] : []),
      ...(Array.isArray(team.members) ? team.members.map((m) => m.userId) : []),
    ]),
    (id) => id.value,
  );
}
